/** Visual style of an administration tab button. */
export type AdminTabVariant = 'pill' | 'bar'

// `pill` — the panels' Active/Inactive segmented switcher
// (companies / agency-structure / tenant-users).
const PILL_BASE =
  'cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-colors'
const PILL_ACTIVE = 'bg-background text-foreground shadow-xs'
const PILL_INACTIVE = 'text-muted-foreground hover:text-foreground'

// `bar` — the modal's mobile tab-bar (underline style).
const BAR_BASE =
  'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors'
const BAR_ACTIVE = 'border-primary text-foreground font-medium'
const BAR_INACTIVE =
  'text-muted-foreground hover:text-foreground hover:border-border border-transparent'

/**
 * Class string for an administration tab button, given whether it is the active
 * tab and the visual variant. Pure replacement for the per-call-site
 * `tabClass()` helpers.
 *
 * The base and state tokens never target the same Tailwind utility, so the
 * plain space-join here is byte-identical to the previous `cn(base, active ? …)`
 * calls while keeping the functional core free of the `@gat-ship/vue-components`
 * barrel (which would drag `.vue` SFCs into the pure unit tests).
 */
export function adminTabClass(
  isActive: boolean,
  variant: AdminTabVariant
): string {
  if (variant === 'bar') {
    return `${BAR_BASE} ${isActive ? BAR_ACTIVE : BAR_INACTIVE}`
  }
  return `${PILL_BASE} ${isActive ? PILL_ACTIVE : PILL_INACTIVE}`
}
